import { useState, useEffect, useCallback } from "react";
import fetchWithToken from "views/auth/signIn/axiosInstance";
import PAGE_IDS from "constants/pageIds";

// Shared locale list + helpers for the post-i18n API, where Category,
// SubCategory, News, and Tag all carry a `translations: [{ locale, ... }]`
// array instead of a single plain-string name/title.
//
// Used as the starting value before /api/language responds, and as a label
// fallback for any locale code the Languages fetch doesn't know about.
export const FALLBACK_LOCALES = [
  { code: "ar", label: "Arabic (العربية)" },
  { code: "en", label: "English" },
];

// The one locale every News/Category/SubCategory/Tag form treats as
// mandatory; every other active language is optional-but-if-started.
export const REQUIRED_LOCALE_CODE = "ar";

export const getTranslatedField = (translations, locale, field = "name") => {
  if (!Array.isArray(translations) || translations.length === 0) return "";
  const match = translations.find((t) => t.locale === locale) || translations[0];
  return match?.[field] || "";
};

// Like getTranslatedField, but returns "" when this exact locale has no
// translation instead of silently falling back to a different one — use
// this wherever showing "missing" is more honest than showing another
// language's value under the wrong label (e.g. a per-locale table cell or
// the "other languages" popover in TranslationsCell).
export const getExactTranslatedField = (translations, locale, field = "name") => {
  if (!Array.isArray(translations)) return "";
  const match = translations.find((t) => t.locale === locale);
  return match?.[field] || "";
};

export const localeLabel = (code) =>
  FALLBACK_LOCALES.find((l) => l.code === code)?.label || code.toUpperCase();

// Converts a server translations array (one row per locale) into a
// { en: {...}, ar: {...}, ... } map that's easier to bind directly to
// per-locale form fields. Missing locales get an empty-but-complete shape.
export const translationsArrayToMap = (translationsArray, emptyShape, locales = FALLBACK_LOCALES) => {
  const map = {};
  locales.forEach(({ code }) => {
    const found = (translationsArray || []).find((tr) => tr.locale === code);
    if (!found) {
      map[code] = emptyShape();
      return;
    }
    // Optional fields (header, metaTitle, metaDesc, ...) come back as `null`
    // from the API when left blank, not `undefined` — a plain spread would
    // let that `null` overwrite emptyShape()'s "" default, and any later
    // `.trim()` on it would crash. Fall back to the empty default per-key
    // instead of a blanket spread.
    const base = emptyShape();
    const merged = { ...found };
    Object.keys(base).forEach((key) => {
      if (merged[key] === null || merged[key] === undefined) merged[key] = base[key];
    });
    map[code] = merged;
  });
  return map;
};

// Fetches the active languages from the Languages admin resource (added
// 2026-07-12) so News/forms pick up a language the moment it's added via
// POST /api/language — no code change or redeploy needed. Falls back to
// FALLBACK_LOCALES while loading or if the request fails (e.g. the caller's
// role lacks read_all on the Languages page), so existing en/ar content
// keeps working either way.
export function useLocales() {
  const [locales, setLocales] = useState(FALLBACK_LOCALES);
  const [loading, setLoading] = useState(true);

  const fetchLocales = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchWithToken(
        `${process.env.REACT_APP_NEWS_API_URL}/language?page_id=${PAGE_IDS.LANGUAGES}`
      );
      // /api/language returns rows in id/creation order (English first, since
      // it was seeded before Arabic) — reorder so the required locale always
      // leads the tab list regardless of seed/creation order, matching
      // FALLBACK_LOCALES' ar-first order above. Sort is stable, so every
      // other locale keeps its original relative order.
      const active = (response?.data || [])
        .filter((l) => l.isActive)
        .map((l) => ({ code: l.code, label: l.name }))
        .sort((a, b) => {
          if (a.code === REQUIRED_LOCALE_CODE) return -1;
          if (b.code === REQUIRED_LOCALE_CODE) return 1;
          return 0;
        });
      if (active.length > 0) setLocales(active);
    } catch (err) {
      console.error("Error fetching languages, falling back to en/ar:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLocales();
  }, [fetchLocales]);

  return { locales, loading };
}

// Manages a { en: {...}, ar: {...}, ... } translations map that stays in
// sync with an async-loaded `locales` list (see useLocales() above) — used
// by Category/SubCategory/Tag/News create+edit forms so a newly-activated
// language always has a slot before its tab is rendered. Read access should
// still go through `translations[code] || emptyShape()` at the render site,
// since `locales` can update one render before this effect catches up.
export function useTranslationsState(locales, emptyShape) {
  const [translations, setTranslations] = useState(() => {
    const init = {};
    locales.forEach(({ code }) => { init[code] = emptyShape(); });
    return init;
  });

  useEffect(() => {
    setTranslations((prev) => {
      const next = { ...prev };
      let changed = false;
      locales.forEach(({ code }) => {
        if (!next[code]) {
          next[code] = emptyShape();
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- emptyShape is a fresh factory each render by design; only `locales` should retrigger this
  }, [locales]);

  const setField = useCallback((code, field, value) => {
    setTranslations((prev) => ({ ...prev, [code]: { ...emptyShape(), ...prev[code], [field]: value } }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [translations, setTranslations, setField];
}

// Validates a simple single-field translations map (Category/SubCategory/Tag
// "name", BotResponse "answer"): the required locale (English) must have a
// non-empty value; every other active locale is optional — it can be added
// or left blank freely.
export function validateNameTranslations(translations, locales, t, field = "name", requiredMessage) {
  const errors = {};
  locales.forEach(({ code }) => {
    const value = translations[code]?.[field] || "";
    if (code === REQUIRED_LOCALE_CODE && !value.trim()) {
      errors[code] = requiredMessage || t("Name is required");
    }
  });
  return errors;
}
